      /**
       * The design's rail: a plain overflow-x scroller with two circular
       * arrows over its edge covers (2026-09-16; in the heading row before
       * that). No dependency on the slideshow
       * component — that one is paged; this one cycles one book at a time.
       *
       * Everything here is enhancement. The markup scrolls without it.
       */
      class SbuShelfRail extends HTMLElement {
        /** @type {AbortController | undefined} */
        #abortController;
        /** @type {ResizeObserver | undefined} */
        #resizeObserver;
        /** @type {IntersectionObserver | undefined} */
        #intersectionObserver;
        /** @type {number} */
        #frame = 0;
        /** @type {number} */
        #timer = 0;
        /** @type {boolean} */
        #onScreen = true;
        /** @type {Set<'hover' | 'press' | 'focus'>} */
        #holds = new Set();
        /** @type {number} requestAnimationFrame id of the running glide, 0 at rest */
        #tween = 0;
        /**
         * @type {number} scrollLeft the running glide is heading for. While a
         * press owns the glide, every arrow press, in either direction, adds
         * or takes away one card here (2026-09-16, A6: presses are a counter).
         * Rotations shift it by a step, so it is always a card boundary.
         */
        #target = 0;
        /**
         * @type {boolean} whether the running glide was started or re-aimed by
         * an arrow press (true) or by the autoplay tick (false). Only a
         * press-owned glide counts presses on top of its target (#move);
         * reset when the glide lands or is cancelled (2026-09-16).
         */
        #pressGlide = false;
        /**
         * @type {Element[]} the list's cards in their authored order, captured
         * on connect. Put back when the rail stops cycling (#restoreOrder,
         * 2026-09-16).
         */
        #order = [];
        /**
         * @type {{ id: number, x: number, left: number, dragging: boolean, lastX: number, lastT: number, v: number } | null}
         * a touch or pen gesture that started on an arrow (#onArrowPointerDown).
         */
        #armDrag = null;
        /**
         * @type {{ id: number, x: number, y: number } | null} a touch or pen
         * that landed on the covers and has not yet moved 6px: where it landed.
         * Cleared once its direction is decided or it lifts (#onGestureMove).
         */
        #gesture = null;
        /**
         * @type {boolean} a finger moving sideways stopped a glide, and
         * snapping stays off until the finger's own scroll begins or, failing
         * that, the touch lifts and the rail is still (#onGestureMove,
         * 2026-09-16).
         */
        #snapDeferred = false;
        /** @type {boolean} a touch is down on this rail (touchstart to the last touchend; 2026-09-16) */
        #fingerDown = false;
        /** @type {number} the scrollLeft the glide last wrote, to tell its scroll events from a finger's (2026-09-16) */
        #written = 0;
        /** @type {number} when a swipe from an arrow last ended; a click within 400ms of it is swallowed */
        #draggedAt = -Infinity;
        /** @type {number} scrollLeft the current glide segment started from */
        #glideFrom = 0;
        /** @type {number} frame-clock time (ms) the current glide segment started */
        #glideStart = 0;
        /** @type {number} length of the current glide segment, ms */
        #glideDuration = 500;
        /** @type {number} the card step the glide's numbers were written in */
        #glideStep = 0;
        /**
         * @type {Element | null} the card at the rail's start edge when it last
         * came to rest. What a resize re-aligns to (2026-09-16, A4).
         */
        #front = null;
        /** @type {number} when the visitor last scrolled the rail themselves */
        #userAt = -Infinity;
        /** @type {number} */
        #settle = 0;

        connectedCallback() {
          // A fresh controller on every connect. The theme editor and the
          // Section Rendering API detach and re-attach this element, and an
          // aborted controller cannot be reused.
          this.#abortController?.abort();
          this.#abortController = new AbortController();
          const { signal } = this.#abortController;

          const rail = this.rail;
          if (!rail) return;

          this.#order = Array.from(rail.children);

          for (const arrow of this.arrows) {
            arrow.hidden = false;
            arrow.addEventListener('click', this.#onArrowClick, { signal });
            // A swipe that starts on a circle scrolls the rail (2026-09-16).
            arrow.addEventListener('pointerdown', this.#onArrowPointerDown, { signal });
            arrow.addEventListener('pointermove', this.#onArrowPointerMove, { signal });
            arrow.addEventListener('pointerup', this.#onArrowPointerUp, { signal });
            arrow.addEventListener('pointercancel', this.#onArrowPointerUp, { signal });
          }

          rail.addEventListener('scroll', this.#onScroll, { passive: true, signal });
          // What counts as the visitor scrolling (2026-09-16, A4): a wheel or
          // trackpad, a finger or pen, a key, or focus moving onto a card. Any
          // other scroll that is not our own glide is the browser's, and the
          // one that matters is a resize re-snapping the rail (#onScroll).
          const touched = () => { this.#userAt = performance.now(); };
          for (const type of ['wheel', 'pointerdown', 'touchstart', 'keydown', 'focusin']) {
            rail.addEventListener(type, touched, { passive: true, signal });
          }

          // #retime as well as #sync: a rail can go from 0x0 to full size when
          // the panel it sits in is revealed (the award panel's year rooms do
          // exactly this), and every measurement taken while it was hidden —
          // the card step, the hidden width, the cycling padding — was taken
          // against nothing. #retime re-runs #fit and re-gates the timer.
          // Observing the content box means the padding #fit writes cannot
          // itself re-trigger this. Then the circles are re-measured and the
          // rail is put back on a whole card (#realign, 2026-09-16, A4).
          //   Until 2026-09-16 the frame's parent was observed too, because
          // #placeArrows capped the frame's width to end the rail on a whole
          // cover and a capped rail stopped reporting a wider column. The cap
          // was removed (operator: no blank space at the rail's end), so the
          // rail's own box follows its column again and the rail is enough.
          // #restoreOrder last: #retime's #fit may just have dropped the
          // padding that kept the rail cycling (2026-09-16).
          //   #sync once more at the end (2026-09-16, rail sweep): the first
          // #sync reads the padding #fit wrote for the OLD width, so a rail
          // that #fit pads (the 8-volume series at 1480-1600, the 10-volume
          // one at 1840-1920) read as bounded at its start and its PREV
          // circle stayed hidden after a resize until something scrolled it.
          const onResize = () => {
            this.#sync();
            this.#retime();
            this.#placeArrows();
            this.#realign();
            this.#restoreOrder();
            this.#sync();
          };
          this.#resizeObserver = new ResizeObserver(onResize);
          this.#resizeObserver.observe(rail);

          // Same reason, for a reveal that changes no box this element owns;
          // also a window resize or orientation change that leaves the rail's
          // box as it was but still re-lays it out.
          window.addEventListener('resize', onResize, { signal });

          // ---- AUTOPLAY (added 2026-09-09) -------------------------------
          // Held while the pointer is over the rail, while keyboard focus is
          // inside it, and while a pointer is down on it. Each of these is a person
          // reading or reaching for a specific book; moving the target out
          // from under them is the classic carousel failure.
          //
          // ONE SET OF REASONS, NOT ONE FLAG (2026-09-14, Y13). With a single
          // boolean, clicking an arrow while hovering released the hold on
          // pointerup and autoplay resumed under a still pointer; tabbing
          // between two cards did the same on the focusout in between. Each
          // reason is now tracked on its own and the timer only restarts once
          // none is left. A synthetic `pointerenter` (the parity probe and
          // hold-rails dispatch one) still adds `hover`.
          //
          // NOTHING BUT A DRAG STOPS A GLIDE IN ITS TRACKS (2026-09-16, two
          // verifier rounds). Every hold stops the timer at once, but a glide
          // already in flight is left to land on its card. Cancelling one gives
          // snapping back, and the rail jumps to the nearest card in one frame.
          // Under the old 900ms in-out easing that jump was under 2px early in
          // a glide, so nobody saw it; the 500ms ease-out (see #glideTo)
          // covers ~45% of a card in the first 80ms, so it became a visible
          // 38-76px jump — first on a touch tap (round 1, which exempted touch
          // only), then on desktop: a mouse moving onto the covers mid-glide,
          // or a second arrow click 150ms after the first. So hover, a press on
          // an arrow (#move folds it into the glide instead) and keyboard focus
          // (the only focus that holds, see below) no longer cancel. Round 2
          // found the same jump on the last hold that still cancelled, a TOUCH
          // OR PEN press on the covers: a tap on a price, or a finger landing
          // to scroll the page, jumped the rail 20-78px. So a press no longer
          // cancels either; only a finger that then moves SIDEWAYS does, and
          // without the jump (#onGestureMove, 2026-09-16). A touch swipe that
          // starts on a circle cancels the glide too, once it moves 8px
          // (#onArrowPointerMove).
          //
          // FOCUS HOLDS ONLY WHEN IT IS KEYBOARD FOCUS (2026-09-16, verifier:
          // one click or tap on an arrow stopped autoplay for good). A click
          // or tap focuses the button, and the focus hold stayed until focus
          // left the rail — so moving the mouse away, or lifting the finger,
          // never let the rail move again. A pointer's own reasons are already
          // `hover` (until pointerleave) and `press` (until pointerup). So
          // focusin holds only when the focused element matches
          // :focus-visible (the keyboard); a key pressed while a
          // pointer-focused control is focused turns it into keyboard focus,
          // so keydown holds too. focusout still releases either way.
          const onArrow = (event) => event.target instanceof Element && !!event.target.closest('[data-sbu-arrow]');
          this.addEventListener('pointerenter', () => this.#hold('hover'), { signal });
          this.addEventListener('pointerleave', () => this.#release('hover', 'press'), { signal });
          this.addEventListener('pointerdown', (event) => {
            const e = /** @type {PointerEvent} */ (event);
            const finger = (e.pointerType === 'touch' || e.pointerType === 'pen') && !onArrow(e);
            this.#gesture = finger ? { id: e.pointerId, x: e.clientX, y: e.clientY } : null;
            this.#hold('press');
          }, { signal });
          // Whether the finger is scrolling the RAIL is decided on its first
          // move past 6px (#onGestureMove). Pointer events for a pen, and for a
          // touch until the browser takes the pan over (pointercancel); touch
          // events for a touch after that, since they keep coming.
          this.addEventListener('pointermove', (event) => {
            const e = /** @type {PointerEvent} */ (event);
            if (this.#gesture && e.pointerId === this.#gesture.id) this.#onGestureMove(e.clientX, e.clientY);
          }, { signal });
          this.addEventListener('touchmove', (event) => {
            const touch = /** @type {TouchEvent} */ (event).touches[0];
            if (touch) this.#onGestureMove(touch.clientX, touch.clientY);
          }, { passive: true, signal });
          this.addEventListener('pointerup', () => {
            this.#gesture = null;
            this.#release('press');
          }, { signal });
          this.addEventListener('pointercancel', () => this.#release('press'), { signal });
          const lifted = (event) => {
            if (/** @type {TouchEvent} */ (event).touches.length > 0) return;
            this.#gesture = null;
            this.#fingerDown = false;
            this.#onScroll();
          };
          this.addEventListener('touchstart', () => { this.#fingerDown = true; }, { passive: true, signal });
          this.addEventListener('touchend', lifted, { passive: true, signal });
          this.addEventListener('touchcancel', lifted, { passive: true, signal });
          const keyboardFocus = (event) => {
            const el = event.target;
            if (el instanceof Element && el.matches(':focus-visible')) this.#hold('focus');
          };
          this.addEventListener('focusin', keyboardFocus, { signal });
          this.addEventListener('keydown', () => {
            if (this.contains(document.activeElement)) this.#hold('focus');
          }, { signal });
          this.addEventListener('focusout', (event) => {
            // Focus moving to another element inside the rail is not a release.
            const next = /** @type {FocusEvent} */ (event).relatedTarget;
            if (next instanceof Node && this.contains(next)) return;
            this.#release('focus');
          }, { signal });

          // Off-screen rails do not animate. Three of these run on the
          // homepage and only one is ever in view.
          if ('IntersectionObserver' in window) {
            this.#intersectionObserver = new IntersectionObserver((entries) => {
              this.#onScreen = entries.some((entry) => entry.isIntersecting);
              this.#retime();
            }, { threshold: 0.2 });
            // Observe the RAIL, not the host. A section that only needs the
            // behaviour wraps its list in a `display: contents` host, which has
            // no box of its own and would never report as intersecting.
            this.#intersectionObserver.observe(rail);
          }

          this.#sync();
          this.#retime();
          // Again after #retime's #fit, for the same reason as onResize (2026-09-16).
          this.#sync();
          this.#placeArrows();
          this.#front = this.#nearestCard();
        }

        disconnectedCallback() {
          this.#abortController?.abort();
          this.#resizeObserver?.disconnect();
          this.#intersectionObserver?.disconnect();
          this.#stop();
          this.#cancelTween();
          if (this.#frame) cancelAnimationFrame(this.#frame);
          clearTimeout(this.#settle);
        }


        /**
         * MAKE AN ALMOST-LONG-ENOUGH RAIL CYCLE (2026-09-09).
         *
         * Cycling needs a full cover of hidden content to hide the rotation
         * behind (see #wrap). The award room holds a 12-title shortlist in a
         * room that fits 11.6 of them, so it hides 85px — a book is visibly cut
         * and yet the rail refused to move. The operator asked three times for
         * that room to move, and they are right to: a sliced cover that never
         * resolves is worse than either extreme.
         *
         * So the shortfall is made up with padding at the end of the list: the
         * rail's own CSS end padding plus `origin + step - hidden`, so the rail
         * can scroll exactly one card past its first card's snap position,
         * rotation becomes valid, and the room cycles like every other.
         *
         * ADDED TO THE CSS PADDING, NOT IN PLACE OF IT (2026-09-16, verifier:
         * product set rail at 1440-1560px). The inline padding used to be
         * `step - hidden` alone, which REPLACED the product set rail's 2px CSS
         * end padding (sections/sbu-product.liquid, .sbu-product__rail) and
         * ignored its 2px start padding (#origin 2). That rail was left
         * hiding step - 2 = 158px where #cycles wanted 159, so the rail #fit
         * had padded stayed bounded: one NEXT ran it to its end with a 109px
         * blank strip, the front card 4px off a boundary, NEXT hidden, and a
         * resize restored the authored order (a jump to book 1). #fit and
         * #cycles now read the same number: the rail cycles when it hides at
         * least `origin + step - 1`, and #fit pads to `origin + step`. On every
         * other rail origin and the CSS end padding are 0, so nothing moved
         * there.
         *
         * WHAT THIS COSTS, stated plainly: at the very end of each glide (its
         * length is #glideTo's `duration`) the padding reaches the right
         * edge, so a blank strip of the page ground shows for a few frames before the
         * rotation resets to zero. It is not a sliver: the strip is as wide as
         * the inline padding, which grows toward a whole card as the overflow
         * shrinks (measured 2026-09-16, verifier: on the product set rail
         * 113px at 1512, 137px at 1536, 161px at 1560; a 112px peak seen
         * mid-glide at 1512). At rest the rail sits on a card edge with no
         * strip. That is the price of cycling a shelf that barely overflows,
         * and it disappears on its own as soon as the room holds a couple
         * more books.
         *
         * THE THREE CASES, and why the bounds matter (`need` = #need()):
         *   hidden <= 2        every book already fits. Nothing is cut, so nothing
         *                      should move. No padding — padding here would invent
         *                      an overflow and set a complete shelf sliding.
         *   2 < hidden < need - 1  a book is cut but the rail cannot rotate. Pad.
         *   hidden >= need - 1     already cycles (#cycles allows the 1px). Any
         *                          padding is cleared, so a rail that grows past
         *                          the threshold drops the crutch.
         * A rail of one card never cycles (#cycles), so it is never padded.
         */
        #fit() {
          const rail = this.rail;
          if (!rail) return;

          // Measure without our own padding in play, or the reading feeds back.
          // Taking it away can clamp the rail's scrollLeft (a rail resting past
          // the unpadded end); the position is put back once the padding is
          // (2026-09-16), or a hold/release would nudge a resting rail.
          const left = rail.scrollLeft;
          const had = rail.style.paddingInlineEnd;
          rail.style.paddingInlineEnd = '';
          // The card width first (2026-10-05, even gaps): every number below
          // is in steps of the sized card. With it, a rail of more books than
          // fit hides a whole number of steps, so this padding is only ever
          // the product set rail's 2px start padding (#need).
          this.#size();
          const step = this.#step();
          const hidden = rail.scrollWidth - rail.clientWidth;
          const need = this.#need();
          let pad = '';
          if (step > 0 && rail.children.length >= 2 && hidden > 2 && hidden < need - 1) {
            // The CSS end padding, read with the inline value cleared, plus the shortfall.
            const base = parseFloat(getComputedStyle(rail).paddingInlineEnd) || 0;
            pad = base + Math.ceil(need - hidden) + 'px';
          }
          rail.style.paddingInlineEnd = pad;
          if (had && Math.abs(rail.scrollLeft - left) > 0.5) {
            const snap = rail.style.scrollSnapType;
            rail.style.scrollSnapType = 'none';
            rail.scrollLeft = left;
            rail.style.scrollSnapType = snap;
          }
        }

        /**
         * WHOLE COVERS AT REST, EVEN GAPS: THE COVERS FLEX (2026-10-05,
         * operator; decisions/2026-10-05-even-gaps-covers-flex-three-books-on-a-phone.md).
         * The gap is the rail's CSS column-gap and never moves. The card
         * width is chosen so a whole number of cards exactly fills the rail's
         * content box: the largest card <= 176px from 750px up, three covers
         * (a fourth once it can be >= 110px) below. The n-th card ends at the
         * rail's visible edge, and the step (#step, card + gap) lands on a
         * whole card. Written as inline custom properties the cells and
         * covers already read (--sbu-rail-track[-phone], --sbu-grid-card,
         * --sbu-bundle-card), so height follows width at 2:3.
         *
         * The maths lives in ONE function, window.sbuRailSpread, defined by
         * an inline script in snippets/sbu-shelf-rail.liquid. That script
         * runs it at parse time, before first paint, so the covers never
         * resize (and move the page) when this deferred module arrives; this
         * method re-runs it on every later re-fit. A rail whose books all fit
         * gets the same card as a full rail at that width (it never
         * overflows, so it never slides), which keeps stacked shelves equal.
         */
        #size() {
          const rail = this.rail;
          // Defined by every render of the snippet, which also loads this module.
          if (rail && typeof window.sbuRailSpread === 'function') window.sbuRailSpread(rail);
        }

        /**
         * THE ONE CYCLING THRESHOLD, shared by #fit and #cycles (2026-09-16):
         * the hidden width a rail needs to scroll one whole card past its
         * first card's snap position — #origin (2 on the product set rail, 0
         * elsewhere) plus one step. #cycles allows 1px under it for subpixel
         * card widths; #fit pads short rails up to it.
         * @returns {number}
         */
        #need() {
          return this.#origin() + this.#step();
        }

        /**
         * Milliseconds between advances, from the section setting. 0 (or a
         * missing attribute) means the rail never moves on its own.
         * @returns {number}
         */
        get #interval() {
          return Number(this.dataset.sbuAutoplay || 0);
        }

        /**
         * Stops the timer. Never a glide in flight (see "NOTHING BUT A DRAG"
         * in connectedCallback; the `cancel` argument that a touch press on
         * the covers used was removed 2026-09-16, #onGestureMove).
         * @param {'hover' | 'press' | 'focus'} reason
         */
        #hold(reason) {
          this.#holds.add(reason);
          this.#stop();
        }

        /**
         * Retimes only when this call empties the set. A pointer leaving
         * mid-press also drops `press`, so a drag released outside the rail
         * cannot leave it held for good.
         * @param {...('hover' | 'press' | 'focus')} reasons
         */
        #release(...reasons) {
          let removed = false;
          for (const reason of reasons) removed = this.#holds.delete(reason) || removed;
          if (removed && this.#holds.size === 0) this.#retime();
        }

        #stop() {
          if (this.#timer) clearInterval(this.#timer);
          this.#timer = 0;
        }

        /**
         * Start or stop the timer against the four conditions that gate it.
         * Called on connect, on hold/release, on visibility change and after
         * every resize, so a rail that stops overflowing stops moving.
         */
        #retime() {
          this.#stop();
          this.#fit();

          const rail = this.rail;
          if (!rail) return;

          // 1. The operator turned it on and gave it an interval.
          if (this.#interval <= 0) return;
          // 2. Somebody is using the rail right now.
          if (this.#holds.size > 0) return;
          // 3. The rail is on screen.
          if (!this.#onScreen) return;
          // 4. THE RAIL CAN ACTUALLY CYCLE — at least one whole cover hidden
          //    past the first card's snap position (#need; since 2026-09-16 it
          //    counts the product set rail's 2px start padding, see #fit).
          //
          //    This number has moved twice and the operator settled it both
          //    times, so the reasoning is worth keeping. It was a whole card,
          //    then dropped to 2px because the award room hid 85px and left a
          //    book permanently sliced, then put back to a whole card on
          //    2026-09-09 when the operator saw what the 2px version actually
          //    did: "the movement right now for the award winners go left <->
          //    right, thats not correct."
          //
          //    They are right, and it is not fixable at this size. Moving one
          //    card forward and cycling needs a full card of hidden content to
          //    hide the cycle behind (see #wrap). A rail with only 85px spare
          //    can travel 85px and then has nowhere to go but back, and a shelf
          //    that slides out and back is not a carousel — it is a twitch.
          //
          //    So a rail that cannot cycle does not move at all. Its arrows
          //    still work and it still scrolls by drag. The fix for such a rail
          //    is more books in it, not more animation.
          if (!this.#cycles()) return;
          // 5. The visitor asked for less motion. WCAG 2.3.3 / 2.2.2: content
          //    that moves on its own is the exact thing this setting is for,
          //    and pointer-hold is not a substitute for honouring it.
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

          this.#timer = setInterval(this.#advance, this.#interval);
        }

        /**
         * ROUND ROBIN — the books cycle, the rail never rewinds (2026-09-09,
         * operator: "should the movement be round robin instead of going back
         * and forth?"). Yes, and this is how.
         *
         * It used to advance one card to the end and then scroll all the way
         * back to zero. That rewind is a long backward sweep that undoes
         * everything the visitor just watched, and on a short rail it degraded
         * into a two-state flicker between the start and the end.
         *
         * Now each tick glides forward exactly one card, and as the glide
         * carries the leftmost card fully out of view that card moves to the
         * end of the list with one card subtracted from scrollLeft in the same
         * frame (#wrap). Those two writes cancel: every visible pixel is
         * unchanged, and the rail is back where it started with a different
         * book at the front. Repeat forever and the shelf drifts in one
         * direction with no seam and no rewind.
         *
         * WHY NOT DUPLICATE THE LIST, which is the other way to fake an endless
         * rail: these are product cards. Each carries a link and an add-to-cart
         * button, so a duplicated set means two of every book in the accessible
         * tree and two buttons that add the same title. Rotating keeps exactly
         * one node per book.
         *
         * A RAIL TOO SHORT TO CYCLE DOES NOT AUTOPLAY. Rotation needs a full
         * card of hidden content to hide the move behind. A shelf with less than
         * that stays still rather than sliding out and back; see gate 4 in
         * #retime for why that is the right trade. Its arrows still work.
         *
         * A tick that finds a glide already moving (an arrow press) skips
         * (2026-09-16, A6): arrow presses add up, and a tick folded in would
         * count as a press nobody made. For the same reason a glide the tick
         * starts is not press-owned, so a press landing in it counts from the
         * card on screen, not on top of the tick's card (#move).
         */
        #advance = () => {
          if (this.#tween) return;
          this.#move(true, false);
        };

        /**
         * THE ROTATION (2026-09-09; moved into the glide 2026-09-16, A6).
         * Given the scrollLeft a frame wants, moves whole cards from one end
         * of the list to the other until that position lies within one card
         * of the rail's start, and returns the position to write instead.
         * Forward, a card goes to the back once it is fully out of view on
         * the left; backward, the last card comes to the front.
         *
         * The DOM move and the scrollLeft that compensates it must land in the
         * SAME frame, which is why the caller writes the returned value at
         * once and never animates it. Snapping must be off (it is for every
         * glide): with it live, Chrome answered an appendChild by re-snapping
         * to the card it last snapped to (measured 2026-09-16: 320 -> 0 where
         * 320 -> 160 was written).
         *
         * Why inside the glide and not after it (2026-09-16, A6): rotating
         * only once a glide had landed capped a glide at the rail's hidden
         * width. A rail that hides one card (the 8-volume series run, which
         * #fit pads to exactly that) could not fold a second press in, so it
         * queued it, stalled 66-118ms between presses and dropped every press
         * beyond one. Rotating as the glide goes keeps the position within one
         * card of the start, so a glide of any length fits any cycling rail.
         *
         * The glide's own numbers (#glideFrom, #target) shift with the cards,
         * so the glide carries on as if nothing moved.
         *
         * @param {number} position
         * @returns {number}
         */
        #wrap(position) {
          const rail = this.rail;
          if (!rail || !this.#cycles()) return position;
          const step = this.#step();
          if (step <= 0) return position;
          const origin = this.#origin();

          let pos = position;
          let guard = rail.children.length;
          while (pos - origin >= step - 0.5 && guard-- > 0) {
            const first = rail.firstElementChild;
            if (!first) break;
            rail.appendChild(first);
            pos -= step;
            this.#glideFrom -= step;
            this.#target -= step;
          }
          while (pos - origin < -0.5 && guard-- > 0) {
            const last = rail.lastElementChild;
            if (!last) break;
            rail.insertBefore(last, rail.firstElementChild);
            pos += step;
            this.#glideFrom += step;
            this.#target += step;
          }
          return pos;
        }

        /**
         * The one path both controls scroll through, so the arrows and the
         * autoplay can never drift apart in behaviour. Glides to `target`
         * (a scrollLeft on a card boundary); a call while a glide runs
         * re-aims it from where the rail is now.
         *
         * WHY THIS IS HAND-ANIMATED rather than `behavior: 'smooth'`
         * (2026-09-09, operator: "make the movement more smooth"). The native
         * smooth scroll is short and briskly eased — right for "jump to the
         * next thing", wrong for a shelf that is meant to drift. Its duration
         * is chosen by the browser and cannot be set, so the only way to make a
         * one-card move read as a glide is to run the tween here.
         *
         * 500ms per card with an ease-out cubic (2026-09-16, operator: "the
         * animation of the carousel … lets make it a bit more faster.. seems
         * like there is a lag"). It was 900ms in-out cubic from 2026-09-09
         * ("make the movement more smooth"). Measured on that version at 390
         * and 1280: no dropped frames — every frame 16.6ms, the rotation frame
         * included — so the "lag" was the easing envelope itself. In-out cubic
         * on a 140px step moved under 2px for the first ~150ms and spent its
         * last ~140ms crawling the final 2px, so an arrow click looked ignored
         * for a quarter second and the shelf looked parked before the
         * rotation. Ease-out starts moving on the first frame and settles by
         * ~400ms. Fallback if this reads as a step rather than a slide:
         * easeInOutSine, `(t) => -(Math.cos(Math.PI * t) - 1) / 2`. The tween
         * fits inside the operator's interval with room to spare at the 2s
         * minimum.
         *
         * PRESSES THAT ADD UP (2026-09-16, A6). A glide of several cards
         * lasts 500ms x sqrt(cards), capped at 1200ms, so five quick presses
         * read as one faster sweep rather than a 2.5s crawl. A re-aimed glide
         * never starts slower than the rail is already moving in that
         * direction (ease-out's opening speed is 3 x distance / duration, so
         * the duration is shortened to match): the second press speeds the
         * shelf up, it never brakes it. That is what "no stall" measures.
         *
         * SNAPPING IS SUSPENDED FOR THE DURATION, and this time with a reason
         * that holds: the tween writes scrollLeft on every frame, and each
         * write is a scroll the snap engine may answer by pulling the rail to
         * the nearest card — which is the frame's own start position. Restoring
         * afterwards cannot jump, because every glide lands on an exact card
         * boundary, which is already a snap point.
         *
         * A NOTE ON DIAGNOSING THIS RAIL, because it cost an hour on
         * 2026-09-09 and the trap is invisible. A remote-controlled Chrome tab
         * can be `document.visibilityState === 'hidden'` while still answering
         * script, taking screenshots and reporting correct geometry. In that
         * state requestAnimationFrame does not fire, so this tween — and any
         * `behavior: 'smooth'` scroll — silently does nothing, while
         * `behavior: 'auto'` works normally. That asymmetry looks exactly like a
         * scroll-snap bug and was briefly written up as one here. Check
         * `document.visibilityState` before concluding anything else.
         *
         * @param {number} target
         */
        #glideTo(target) {
          const rail = this.rail;
          if (!rail) return;
          const step = this.#step();
          if (step <= 0) return;

          // A rail too short to rotate keeps plain bounded scrolling: the
          // target stops at each end and #sync greys the arrow out there.
          if (!this.#cycles()) {
            target = Math.max(0, Math.min(target, rail.scrollWidth - rail.clientWidth));
          }

          // Reduced motion gets the destination with no journey.
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.#cancelTween();
            this.#target = target;
            rail.style.scrollSnapType = 'none';
            rail.scrollLeft = this.#wrap(target);
            rail.style.scrollSnapType = '';
            this.#front = this.#nearestCard();
            return;
          }

          const from = rail.scrollLeft;
          const distance = Math.abs(target - from);
          if (!this.#tween && distance < 0.5) return;

          // The glide's clock is the frame clock (2026-09-16, A6): a press lands
          // between frames, and the next frame's timestamp is when that frame
          // BEGAN — often before performance.now() at the press — so a glide
          // timed from the press sat still for its first frame (measured: a
          // 17ms stall on every re-aimed glide). Timed from the current frame,
          // the next frame is always one frame in.
          const now = document.timeline?.currentTime ?? performance.now();
          let duration = Math.min(1200, 500 * Math.max(1, Math.sqrt(distance / step)));
          if (this.#tween) {
            const t = Math.max(0, Math.min(1, (now - this.#glideStart) / this.#glideDuration));
            const velocity = (3 * Math.pow(1 - t, 2) * (this.#target - this.#glideFrom)) / this.#glideDuration;
            if (velocity !== 0 && Math.sign(velocity) === Math.sign(target - from) && distance > 0) {
              duration = Math.max(200, Math.min(duration, (3 * distance) / Math.abs(velocity)));
            }
          }

          this.#glideFrom = from;
          this.#target = target;
          this.#glideStart = now;
          this.#glideDuration = duration;
          this.#glideStep = step;
          this.#snapDeferred = false;
          rail.style.scrollSnapType = 'none';
          if (!this.#tween) this.#tween = requestAnimationFrame(this.#glideFrame);
        }

        /** One frame of the running glide. @param {number} now */
        #glideFrame = (now) => {
          const rail = this.rail;
          if (!rail) {
            this.#tween = 0;
            return;
          }

          // THE STEP CHANGED UNDER THE GLIDE (2026-09-16, A4): a resize across
          // a breakpoint that changes the card width (bundle deals: 120 under
          // 750, 140 above). The glide's numbers are re-expressed in cards of
          // the new width, so it still lands on the card it was heading for.
          const step = this.#step();
          if (step > 0 && this.#glideStep > 0 && Math.abs(step - this.#glideStep) > 0.5) {
            const origin = this.#origin();
            const scale = step / this.#glideStep;
            this.#glideFrom = origin + (this.#glideFrom - origin) * scale;
            this.#target = origin + (this.#target - origin) * scale;
            this.#glideStep = step;
          }

          // Clamped at 0 as well as 1 (2026-09-16, recheck R4): the first
          // frame's timestamp is when that frame began, which can be a few ms
          // BEFORE the glide started. Ease-out cubic at a negative t is
          // negative, so the first frame overshot backwards from the glide — an
          // 11px flick the wrong way before PREV slid left (measured 160 -> 171
          // -> 154).
          const progress = Math.max(0, Math.min(1, (now - this.#glideStart) / this.#glideDuration));
          const ease = 1 - Math.pow(1 - progress, 3);
          rail.scrollLeft = this.#wrap(this.#glideFrom + (this.#target - this.#glideFrom) * ease);
          this.#written = rail.scrollLeft;

          if (progress < 1) {
            this.#tween = requestAnimationFrame(this.#glideFrame);
            return;
          }

          this.#tween = 0;
          this.#pressGlide = false;
          // Snapping comes back only now, on the card the glide landed on (see
          // #wrap for why it must stay off while cards move).
          rail.style.scrollSnapType = '';
          this.#front = this.#nearestCard();
          // A resize during the glide may have ended the cycling (2026-09-16).
          this.#restoreOrder();
        };

        /**
         * Stops a glide mid-flight and gives snapping back. Called when a swipe
         * that started on a circle begins to drag (#onArrowPointerMove), so a
         * drag is never fought by an animation still running underneath it,
         * when a reduced-motion jump replaces it, and on disconnect. A finger
         * swiping the covers stops a glide without giving snapping back
         * (#onGestureMove, 2026-09-16); every hold lets the glide land.
         */
        #cancelTween() {
          this.#snapDeferred = false;
          if (!this.#tween) return;
          cancelAnimationFrame(this.#tween);
          this.#tween = 0;
          this.#pressGlide = false;

          const rail = this.rail;
          if (rail) rail.style.scrollSnapType = '';
        }

        /**
         * A FINGER ON THE COVERS STOPS A GLIDE ONLY ONCE IT SCROLLS THE RAIL
         * SIDEWAYS (2026-09-16, verifier round 2). Until then a touch or pen
         * press is a hold like any other: the timer stops and a glide in
         * flight lands on its card. Stopping the glide on pointerdown gave
         * snapping back at once, so a tap on a price or "+ Add", or a finger
         * landing on the rail to scroll the PAGE, made the rail jump 20-78px
         * to the nearest card in one frame (measured at 390 touch).
         *
         * The first move past 6px decides. Mostly vertical: the page scrolls
         * and the glide carries on. Mostly sideways: the finger takes the rail
         * over and the glide stops where it is. Snapping stays OFF at that
         * moment (turning it back on before the browser's pan starts snapped
         * the rail 24-63px in one frame, measured) and comes back on the
         * finger's first scroll (#onScroll), so the browser snaps the release
         * with its own animation; if the finger never scrolls the rail (a
         * bounded rail at its end), #settleSnap puts it on a card once the
         * touch is lifted and the rail is still.
         * @param {number} x
         * @param {number} y
         */
        #onGestureMove(x, y) {
          const gesture = this.#gesture;
          if (!gesture) return;
          const dx = Math.abs(x - gesture.x);
          const dy = Math.abs(y - gesture.y);
          if (dx < 6 && dy < 6) return;
          this.#gesture = null;
          if (dx <= dy || !this.#tween) return;
          cancelAnimationFrame(this.#tween);
          this.#tween = 0;
          this.#pressGlide = false;
          this.#userAt = performance.now();
          this.#snapDeferred = true;
        }

        /**
         * Snapping that #onGestureMove left off and no scroll gave back: once
         * no touch is down and no glide runs, the rail glides to its nearest
         * card (which gives snapping back as it lands), or simply has
         * snapping back if it is already on one. Never a one-frame snap.
         */
        #settleSnap() {
          if (!this.#snapDeferred || this.#fingerDown || this.#tween) return;
          this.#snapDeferred = false;
          const rail = this.rail;
          if (!rail) return;
          const step = this.#step();
          if (step > 0) {
            const origin = this.#origin();
            this.#glideTo(origin + Math.round((rail.scrollLeft - origin) / step) * step);
          }
          if (!this.#tween) rail.style.scrollSnapType = '';
        }

        /**
         * WHERE THE TWO CIRCLES SIT (2026-09-16, operator: "an arrow in a
         * circle, but in the placement that it goes over the books"). They are
         * absolute inside .sbu-rail-frame (snippets/sbu-shelf-arrows.liquid)
         * and this writes the four custom properties that CSS reads.
         *
         * Vertically: centred on the COVER, not the card — the title and price
         * under it are not part of the target. The cover's box is measured
         * (--sbu-rail-cover-top / --sbu-rail-cover-h), not computed from the
         * track: the track is a per-section setting, the product rail pads 2px
         * above its cards and the bundles page can draw a "cover pending" tile.
         *
         * Horizontally: --sbu-rail-arrow-start / -end are where the rail's
         * VISIBLE left and right edges (the scroller's padding box, which is
         * what clips the cards) sit inside the frame. The stylesheet adds the
         * inset, so every rail at every width puts its circles the same
         * distance inside its own edges (2026-09-16, operator: identical
         * across All Books, Award Winners, the series runs and the set rail).
         *
         * NO FRAME TRIM, AND NO CUT COVER (corrected 2026-10-05, operator,
         * decisions/2026-10-05-even-gaps-covers-flex-three-books-on-a-phone.md).
         * The frame is never resized: the rail runs to its container's edge
         * (a width cap that ended it on a whole cover left blank ground and
         * was removed 2026-09-16). The 2026-09-16 rule that the cover under
         * the right circle may be cut is REVERSED: #size flexes the covers
         * so the last visible cover ends exactly at the rail's visible edge.
         *
         * Custom properties inherit through the display:contents host to the
         * buttons.
         */
        #placeArrows() {
          const rail = this.rail;
          const frame = rail?.closest('.sbu-rail-frame');
          if (!rail || !frame || !this.contains(frame)) return;
          const cover =
            rail.querySelector('li :is(.sbu-card__cover, .sbu-bundles__cover)') ?? rail.querySelector('li > *');
          if (!cover) return;

          const f = frame.getBoundingClientRect();
          const c = cover.getBoundingClientRect();
          const r = rail.getBoundingClientRect();
          if (c.height <= 0 || r.width <= 0) return;

          const viewStart = r.left - f.left + rail.clientLeft;
          const viewEnd = viewStart + rail.clientWidth;

          this.style.setProperty('--sbu-rail-cover-top', `${Math.round(c.top - f.top)}px`);
          this.style.setProperty('--sbu-rail-cover-h', `${Math.round(c.height)}px`);
          this.style.setProperty('--sbu-rail-arrow-start', `${Math.round(viewStart)}px`);
          this.style.setProperty('--sbu-rail-arrow-end', `${Math.round(f.width - viewEnd)}px`);
        }

        /**
         * BACK ON A WHOLE CARD AFTER A RESIZE (2026-09-16, A4, operator:
         * "sometimes when we change the window size the button shows the
         * previous book cover"). At rest the rail sits exactly on a card, and
         * the card before it — the one the round robin keeps hidden just left
         * of the edge — must never peek in under the left circle. A resize can
         * break that: the browser re-snaps the rail to the element it last
         * snapped to, which the rotation may have moved to the other end of
         * the list, and the hidden width it clamps against changes with the
         * rail. So after every resize the rail is put back on the card that was
         * at its start edge when it last came to rest (#front), in place: no
         * restart, no jump back to the first book, no glide.
         *
         * A glide in flight is left alone: its target is always a card
         * boundary, #wrap keeps its position inside the scrollable range at any
         * width, and #glideFrame re-expresses it if the card width changed.
         * A finger on the rail is left alone too, and so is a rail the visitor
         * scrolled in the last second (a swipe's momentum or a trackpad): their
         * card is not recorded until the scroll settles (#onScroll), so a
         * resize or reveal in that window must not pull them back.
         */
        #realign() {
          const rail = this.rail;
          if (!rail || this.#tween || this.#holds.has('press') || rail.clientWidth <= 0) return;
          if (performance.now() - this.#userAt < 1000) return;
          const step = this.#step();
          if (step <= 0) return;
          const hidden = rail.scrollWidth - rail.clientWidth;
          if (hidden <= 1) return;

          const origin = this.#origin();
          const front = this.#front && this.#front.parentElement === rail ? this.#front : this.#nearestCard();
          if (!front) return;
          const index = Array.prototype.indexOf.call(rail.children, front);
          let want = origin + index * step;
          if (!this.#cycles()) want = Math.max(0, Math.min(want, hidden));
          if (Math.abs(rail.scrollLeft - want) < 0.5 && want <= hidden) return;

          rail.style.scrollSnapType = 'none';
          rail.scrollLeft = this.#wrap(want);
          rail.style.scrollSnapType = '';
          this.#front = front;
        }

        /** @returns {HTMLElement | null} */
        get rail() {
          return this.querySelector('[data-sbu-rail]');
        }

        /** @returns {HTMLButtonElement[]} */
        get arrows() {
          return Array.from(this.querySelectorAll('[data-sbu-arrow]'));
        }

        /**
         * ONE CARD plus one gap. Measured, never assumed: the card width is
         * sized per rail width (#size, 2026-10-05) and the gap is the
         * section's, so a step always lands on a whole card.
         *
         * A PAGE-SIZED STEP WAS TRIED AND REJECTED (2026-09-09). It advanced by
         * as many whole cards as fit minus one — eleven cards on a 1621px rail —
         * on the reasoning that a one-card nudge does not read as motion.
         * Operator: "i dont think the click should move 11 cards." One card is
         * the step, for the arrows and for the autoplay both, and the rail
         * therefore reads as one continuous run of books rather than as a set
         * of pages. Do not reintroduce a page step for the arrows alone: the
         * two controls moving by different amounts is worse than either.
         * @returns {number}
         */
        #step() {
          const rail = this.rail;
          if (!rail) return 0;

          const card = rail.querySelector('li');
          const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
          const width = card ? card.getBoundingClientRect().width : 0;

          return width > 0 ? width + gap : rail.clientWidth * 0.8;
        }

        /**
         * The scrollLeft at which the list's first card sits at the rail's
         * start edge — its snap position. 0 on every rail; the product set
         * rail's padding was 2 here until its scroll-padding-inline matched
         * the padding (2026-10-05, even-gaps fix round).
         * @returns {number}
         */
        #origin() {
          const rail = this.rail;
          const first = rail?.firstElementChild;
          if (!rail || !first) return 0;
          const padStart = parseFloat(getComputedStyle(rail).scrollPaddingInlineStart) || 0;
          const left = first.getBoundingClientRect().left - rail.getBoundingClientRect().left - rail.clientLeft;
          return Math.max(0, left + rail.scrollLeft - padStart);
        }

        /**
         * Whether the rail round-robins: at least #need() - 1 hidden (a whole
         * card past the first card's snap position, after #fit) and more than
         * one card to rotate. The same threshold #fit pads to (2026-09-16).
         * @returns {boolean}
         */
        #cycles() {
          const rail = this.rail;
          if (!rail || rail.children.length < 2) return false;
          const step = this.#step();
          return step > 0 && rail.scrollWidth - rail.clientWidth >= this.#need() - 1;
        }

        /**
         * The card whose left edge is nearest the rail's start edge: the book
         * "in front" on screen right now.
         * @returns {Element | null}
         */
        #nearestCard() {
          const rail = this.rail;
          if (!rail) return null;
          const edge = rail.getBoundingClientRect().left + rail.clientLeft;
          let best = null;
          let bestDistance = Infinity;
          for (const card of rail.children) {
            const distance = Math.abs(card.getBoundingClientRect().left - edge);
            if (distance < bestDistance) {
              best = card;
              bestDistance = distance;
            }
          }
          return best;
        }

        /** @param {MouseEvent} event */
        #onArrowClick = (event) => {
          const arrow = event.currentTarget;
          if (!(arrow instanceof HTMLElement)) return;
          // aria-disabled keeps the control focusable but inert, so the click
          // has to be refused here rather than by the disabled attribute.
          if (arrow.getAttribute('aria-disabled') === 'true') return;
          // The click a browser may still send after a swipe from the circle.
          if (performance.now() - this.#draggedAt < 400) return;
          this.#move(arrow.dataset.sbuArrow !== 'prev', true);
        };

        /**
         * ONE PRESS, ONE CARD (rewritten 2026-09-16, A6; operator decisions).
         *
         *   · A press with no glide running, or into a glide the autoplay tick
         *     started, moves one card from what is ON SCREEN at the moment of
         *     the press: the card nearest the rail's start edge (#origin + a
         *     whole number of steps, rounded). So a tap early in an autoplay
         *     glide, while the old card is still in front, re-aims to the card
         *     the tick was already heading for (+1 from the screen, not +2);
         *     a tap late in it goes one card past.
         *   · Presses are a COUNTER (2026-09-16, verifier: mixed presses were
         *     lost). Once a press owns the glide, each further press adds one
         *     card to where it is heading (NEXT) or takes one away (PREV), in
         *     either direction: NNNP is +2, NNP is +1, 10N is +10. So a PREV
         *     late in a one-card NEXT glide lands back on the card that just
         *     left — net zero, never two cards back. This replaces the same
         *     day's "a press against the glide counts from the card on screen"
         *     rule, which dropped the queued presses (NNNP gave 0), and before
         *     that "count from the card the glide started on" (O2), which sent
         *     a late PREV back two cards.
         *   · Either way the press re-aims the running glide (#glideTo), so the
         *     eye sees one continuous motion. There is no queue: #wrap rotates
         *     cards as the glide goes, so no target is ever out of reach.
         *
         * @param {boolean} forward
         * @param {boolean} press an arrow press (true) or the autoplay tick
         */
        #move(forward, press) {
          const rail = this.rail;
          if (!rail) return;
          const step = this.#step();
          if (step <= 0) return;

          const dir = forward ? 1 : -1;
          if (press && this.#tween && this.#pressGlide) {
            this.#glideTo(this.#target + dir * step);
            return;
          }

          const origin = this.#origin();
          const onScreen = origin + Math.round((rail.scrollLeft - origin) / step) * step;
          this.#glideTo(onScreen + dir * step);
          if (press && this.#tween) this.#pressGlide = true;
        }

        /**
         * A SWIPE THAT STARTS ON A CIRCLE (2026-09-16, operator: "swiping the
         * rail still scrolls (only the circle catches taps)"). The circles sit
         * outside the scroller (snippets/sbu-shelf-arrows.liquid), so the
         * browser has nothing to pan when a finger lands on one. Their CSS
         * gives up horizontal panning (touch-action: pan-y pinch-zoom) and
         * this forwards the gesture: once a touch or pen moves 8px sideways it
         * is a drag, the glide stops as it does for a finger swiping the covers,
         * the rail follows the finger (rotating as it goes on a cycling rail),
         * and on release it glides to the nearest card, a flick carrying it
         * one card further. A click within 400ms of the release is swallowed. A mouse
         * is not forwarded: a mouse drag on a button is not a swipe.
         * @param {PointerEvent} event
         */
        #onArrowPointerDown = (event) => {
          const rail = this.rail;
          if (!rail || (event.pointerType !== 'touch' && event.pointerType !== 'pen')) return;
          this.#armDrag = {
            id: event.pointerId,
            x: event.clientX,
            left: rail.scrollLeft,
            dragging: false,
            lastX: event.clientX,
            lastT: event.timeStamp,
            v: 0,
          };
        };

        /** @param {PointerEvent} event */
        #onArrowPointerMove = (event) => {
          const drag = this.#armDrag;
          const rail = this.rail;
          if (!drag || !rail || event.pointerId !== drag.id) return;
          const dx = event.clientX - drag.x;
          if (!drag.dragging) {
            if (Math.abs(dx) < 8) return;
            drag.dragging = true;
            this.#cancelTween();
            this.#userAt = performance.now();
            try {
              /** @type {Element} */ (event.currentTarget).setPointerCapture(event.pointerId);
            } catch {}
            rail.style.scrollSnapType = 'none';
          }
          const dt = event.timeStamp - drag.lastT;
          if (dt > 0) drag.v = (event.clientX - drag.lastX) / dt;
          drag.lastX = event.clientX;
          drag.lastT = event.timeStamp;
          this.#userAt = performance.now();

          let want = drag.left - dx;
          if (!this.#cycles()) want = Math.max(0, Math.min(want, rail.scrollWidth - rail.clientWidth));
          const before = want;
          const pos = this.#wrap(want);
          // A rotation moved the cards by (before - pos); keep the finger's
          // anchor in the same frame of reference.
          drag.left -= before - pos;
          rail.scrollLeft = pos;
        };

        /** @param {PointerEvent} event */
        #onArrowPointerUp = (event) => {
          const drag = this.#armDrag;
          const rail = this.rail;
          if (!drag || event.pointerId !== drag.id) return;
          this.#armDrag = null;
          if (!drag.dragging || !rail) return;
          this.#draggedAt = performance.now();
          const step = this.#step();
          if (step <= 0) {
            rail.style.scrollSnapType = '';
            return;
          }
          const origin = this.#origin();
          // A flick (over 0.3px/ms) carries on to the next card in its
          // direction; a slow release settles on the nearest one.
          let index = (rail.scrollLeft - origin) / step;
          index = Math.abs(drag.v) > 0.3 ? (drag.v < 0 ? Math.ceil(index) : Math.floor(index)) : Math.round(index);
          this.#glideTo(origin + index * step);
          if (!this.#tween) rail.style.scrollSnapType = '';
          this.#front = this.#nearestCard();
        };

        /**
         * Arrow state on every scroll, and (2026-09-16, A4) what a scroll that
         * nobody asked for means. A scroll with no glide running and no recent
         * wheel, touch, key or focus on the rail is the browser's own — a
         * resize re-snapping it — so the rail is put back on its card
         * (#realign). A scroll the visitor made records the card they left in
         * front once it settles.
         */
        #onScroll = () => {
          if (this.#frame) cancelAnimationFrame(this.#frame);
          this.#frame = requestAnimationFrame(() => this.#sync());
          if (this.#tween) return;
          // The first scroll the FINGER makes after it stopped a glide (not the
          // late scroll event of the glide's own last frame, which still reads
          // #written): the browser's pan is now under way, so snapping can come
          // back and the browser snaps the release with its own animation
          // (#onGestureMove).
          const rail = this.rail;
          if (this.#snapDeferred && this.#fingerDown && rail && Math.abs(rail.scrollLeft - this.#written) > 0.5) {
            this.#snapDeferred = false;
            rail.style.scrollSnapType = '';
          }

          const now = performance.now();
          const byVisitor = now - this.#userAt < 1000;
          // Momentum after a swipe keeps counting as the visitor's scroll.
          if (byVisitor) this.#userAt = now;
          clearTimeout(this.#settle);
          this.#settle = window.setTimeout(() => {
            if (this.#tween) return;
            this.#settleSnap();
            if (byVisitor) this.#front = this.#nearestCard();
            else this.#realign();
          }, 150);
        };

        /**
         * Arrow state. Two regimes, and the difference is whether the rail can
         * rotate (2026-09-09):
         *
         *   cycling  — a rail with at least a card hidden has no first or last
         *              book any more, because the list rotates under it. Both
         *              arrows stay live; greying one out at "the end" would be
         *              a lie, and the end it tested for no longer exists.
         *              Once it stops cycling it has ends again, and the
         *              authored order comes back (#restoreOrder, 2026-09-16) —
         *              called after a resize's #fit, not from here: #sync runs
         *              before #fit re-pads the rail, so a rail that still
         *              cycles can read as not cycling for that one call.
         *   bounded  — a rail too short to rotate still has real ends, and an
         *              arrow that cannot move is still greyed out there.
         *
         * A rail that does not overflow at all disables both, as before: with
         * five covers in a wide column they would read as controls that do
         * nothing.
         */
        #sync() {
          const rail = this.rail;
          if (!rail) return;

          const overflows = rail.scrollWidth - rail.clientWidth > 1;
          // A resize can take a rail in or out of overflow; #retime() re-checks
          // the gate rather than leaving a timer running against a rail that
          // now fits. #retime() itself never calls #sync(), so this cannot loop.
          if (!overflows && this.#timer) this.#stop();
          const cycles = this.#cycles();
          const atStart = rail.scrollLeft <= 1;
          const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;

          for (const arrow of this.arrows) {
            const isPrev = arrow.dataset.sbuArrow === 'prev';
            const off = !overflows || (!cycles && (isPrev ? atStart : atEnd));
            arrow.setAttribute('aria-disabled', off ? 'true' : 'false');
          }
        }

        /**
         * THE AUTHORED ORDER COMES BACK WHEN THE RAIL STOPS CYCLING (2026-09-16,
         * verifier). Rotation reorders the list; on a rail that then widens
         * until it no longer overflows, the arrows hide and autoplay stops, so
         * nothing would ever rotate it back and a numbered series read volume
         * 4 first. A rail that does not cycle is put back in its connect-time
         * order at its start. Called at the end of a resize (after #fit) and
         * when a glide lands. Not during a glide or a finger on the rail, and
         * not while focus is on a card inside the list (reordering would move
         * the focused card out from under a keyboard user; a focused arrow
         * does not count, it is hidden once the rail stops overflowing); a
         * later resize or landing tries again. A cycling rail is left as it is: it has no first card.
         */
        #restoreOrder() {
          const rail = this.rail;
          const order = this.#order;
          if (!rail || order.length === 0 || this.#cycles()) return;
          if (this.#tween || this.#armDrag || this.#holds.has('press')) return;
          if (rail.contains(document.activeElement)) return;
          if (order.length !== rail.children.length || order.some((card) => card.parentElement !== rail)) return;
          if (order.every((card, i) => rail.children[i] === card)) return;

          for (const card of order) rail.appendChild(card);
          rail.style.scrollSnapType = 'none';
          // The first card's snap position (#origin; 0 on every rail since 2026-10-05).
          rail.scrollLeft = this.#origin();
          rail.style.scrollSnapType = '';
          this.#front = this.#nearestCard();
        }
      }

      // Inline module scripts run once per section instance, and this section
      // is placed several times on the homepage. Define once.
      if (!customElements.get('sbu-shelf-rail')) {
        customElements.define('sbu-shelf-rail', SbuShelfRail);
      }
